<template>
  <div
    :class="[
      'badge',
      sizeClass,
      colorClass,
      { 'badge-dash': dashed },
      { 'badge-solid': solid },
      { 'badge-outlined': outlined },
      $attrs.class,
    ]"
  >
    <span
      v-if="$slots.prefixIcon"
      class="badge-prefix-icon"
    >
      <slot name="prefixIcon" />
    </span>
    <slot />
    <span
      v-if="$slots.suffixIcon"
      class="badge-suffix-icon"
    >
      <slot name="suffixIcon" />
    </span>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { semanticColors } from '@/constants/colors';

const props = defineProps({
  size: {
    type: String,
    default: 'md',
    validator(value) {
      return ['sm', 'md', 'lg'].includes(value);
    },
  },
  color: {
    type: String,
    validator(value) {
      return semanticColors.includes(value);
    },
  },
  dashed: {
    type: Boolean,
    default: false,
  },
  solid: {
    type: Boolean,
    default: false,
  },
  outlined: {
    type: Boolean,
    default: false,
  },
});

const sizeClass = computed(() => {
  return {
    sm: 'badge-sm',
    md: 'badge-md',
    lg: 'badge-lg',
  }[props.size];
});

const colorClass = computed(() => {
  return {
    primary: 'badge-primary',
    secondary: 'badge-secondary',
    neutral: 'badge-neutral',
    info: 'badge-info',
    success: 'badge-success',
    warning: 'badge-warning',
    error: 'badge-error',
  }[props.color];
});
</script>

<style>
@reference '@/assets/main.css';

.badge {
  --badge-fg: var(--color-gray-700);
  --badge-bg: var(--badge-color, var(--color-gray-50));
  --border-color: var(--color-gray-200);
  @apply font-medium border-[var(--border-color)] whitespace-nowrap leading-none;

  &.badge-sm {
    --size: calc(var(--size-selector, 0.25rem) * 5.5);
    @apply gap-1;
  }

  &.badge-md {
    @apply gap-1;
  }

  &.badge-lg {
    font-size: 0.875rem;
    padding-inline: calc(0.25rem * 3 - var(--border));
  }

  &.badge-dash {
    --border-color: var(--color-gray-500);
    border-color: var(--border-color);
  }

  &.badge-outlined {
    --badge-color: var(--color-white);
    --border-color: var(--badge-fg);
  }

  &.badge-neutral {
    --badge-color: var(--color-gray-50);
    --border-color: var(--color-gray-200);
    --badge-fg: var(--color-gray-700);
  }

  &.badge-secondary {
    --badge-color: var(--color-white);
    --border-color: var(--color-gray-200);
    --badge-fg: var(--color-gray-700);
  }

  &.badge-primary {
    &:not(.badge-solid) {
      --badge-fg: var(--color-brand-700);
    }

    &:not(.badge-solid):not(.badge-outlined) {
      --badge-color: var(--color-brand-50);
      --border-color: var(--color-brand-200);
    }
    &.badge-solid {
      --badge-color: var(--color-brand-600);
      --border-color: var(--color-brand-700);
      --badge-fg: var(--color-white);
    }

    &.badge-dash {
      --border-color: var(--color-brand-500);
    }
  }

  &.badge-success {
    &:not(.badge-solid) {
      --badge-fg: var(--color-success-700);
    }

    &:not(.badge-solid):not(.badge-outlined) {
      --badge-color: var(--color-success-50);
      --border-color: var(--color-success-200);
    }
    &.badge-solid {
      --badge-color: var(--color-success-600);
      --border-color: var(--color-success-700);
      --badge-fg: var(--color-white);
    }

    &.badge-dash {
      --border-color: var(--color-success-500);
    }
  }

  &.badge-warning {
    &:not(.badge-solid) {
      --badge-fg: var(--color-warning-700);
    }

    &:not(.badge-solid):not(.badge-outlined) {
      --badge-color: var(--color-warning-50);
      --border-color: var(--color-warning-200);
    }
    &.badge-solid {
      --badge-color: var(--color-warning-600);
      --border-color: var(--color-warning-700);
      --badge-fg: var(--color-white);
    }
    &.badge-dash {
      --border-color: var(--color-warning-500);
    }
  }

  &.badge-error {
    &:not(.badge-solid) {
      --badge-fg: var(--color-error-700);
    }

    &:not(.badge-solid):not(.badge-outlined) {
      --badge-color: var(--color-error-50);
      --border-color: var(--color-error-200);
    }
    &.badge-solid {
      --badge-color: var(--color-error-600);
      --border-color: var(--color-error-700);
      --badge-fg: var(--color-white);
    }
    &.badge-dash {
      --border-color: var(--color-error-500);
    }
  }
}

.badge-prefix-icon {
  @apply -ml-1;
}

.badge-suffix-icon {
  @apply -mr-1;
}
</style>
